import { z } from 'zod';
import { commissionDealsSchema } from '../pricing/commission-rates';
import { isValidAllowlistEntry } from '../utils/ip-allowlist';

export const SUBDOMAIN_REGEX = /^[a-z0-9]+$/;

export const onboardingDocumentationCategoryValues = [
  'onboarding',
  'release_notes',
  'tutorials',
  'faq',
] as const;

export const onboardingDocumentationCategorySchema = z.enum(
  onboardingDocumentationCategoryValues
);

export type OnboardingDocumentationCategory = z.infer<
  typeof onboardingDocumentationCategorySchema
>;

export const onboardingDocumentSchema = z.object({
  category: onboardingDocumentationCategorySchema,
  title: z.string(),
  description: z.string(),
  order: z.number(),
  imageUrl: z.string(),
  url: z.string(),
});

export type OnboardingDocument = z.infer<typeof onboardingDocumentSchema>;

export const onboardingDocumentationSchema = z.object({
  order: z.number(),
  title: z.string(),
  documents: z.array(onboardingDocumentSchema).optional(),
});

export type OnboardingDocumentation = z.infer<
  typeof onboardingDocumentationSchema
>;

export const updateOnboardingDocumentInput = z.object({
  category: z.string(),
  title: z.string(),
  description: z.string(),
  order: z.number(),
  imageUrl: z.string(),
  url: z.string(),
});

export type UpdateOnboardingDocumentInput = z.infer<
  typeof updateOnboardingDocumentInput
>;

export const updateOnboardingDocumentationInput = z.object({
  order: z.number(),
  oldTitle: z.string(),
  newTitle: z.string(),
  documents: z.array(updateOnboardingDocumentInput).optional(),
});

export type UpdateOnboardingDocumentationInput = z.infer<
  typeof updateOnboardingDocumentationInput
>;

const publishTimeSpanSchema = z.object({
  days: z.number().int().nonnegative().optional(),
  hours: z.number().int().nonnegative().optional(),
  minutes: z.number().int().nonnegative().optional(),
});

const itineraryValueCategorySchema = z
  .object({
    title: z.string(),
    color: z.string().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
  })
  .loose();

// Two shapes coexist in stored data and both must round-trip:
//  - FLAT (what the current form writes): `{ Accommodation: '#2E86AB', … }`
//  - LEGACY NESTED (v2 rows): `{ content_categories: { Accommodation: { color: '#2E86AB' } } }`
// The form reads only the flat keys, but it echoes the whole object back on
// save, so rejecting the legacy shape blocks every save for those orgs.
const colorPaletteSettingsSchema = z.record(
  z.string(),
  z.union([z.string(), z.record(z.string(), z.unknown())])
);

// Legacy rows store an explicit JSON `null` for a hero slot that was never
// filled, so `null` must round-trip as "unset" — rejecting it would make every
// save from such an org fail on a key the form does not even surface.
const heroMediaItemSchema = z.object({
  heroImage: z.string().nullable().optional(),
  heroVideo: z.string().nullable().optional(),
});

const companyInfoLineSchema = z
  .object({
    label: z.string().optional(),
    value: z.string().optional(),
  })
  .loose();

const organizationLanguageSchema = z
  .object({
    primary: z.string().optional(),
    secondary: z.string().optional(),
  })
  .loose();

const budgetValueThresholdsSchema = z
  .object({
    tier1Max: z.number().min(0).optional(),
    tier2Max: z.number().min(0).optional(),
    tier3Max: z.number().min(0).optional(),
  })
  .loose()
  .superRefine((thresholds, ctx) => {
    const { tier1Max, tier2Max, tier3Max } = thresholds;
    if (
      typeof tier1Max !== 'number' ||
      typeof tier2Max !== 'number' ||
      typeof tier3Max !== 'number'
    ) {
      return;
    }
    if (!(tier1Max < tier2Max && tier2Max < tier3Max)) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Budget value thresholds must satisfy tier1Max < tier2Max < tier3Max',
        path: ['tier1Max'],
      });
    }
  });

// `null` counts as UNSET, exactly like an absent key: legacy rows store an
// explicit null for a budget cut that was never configured, and an org must be
// able to save unrelated settings without first filling in a valid 100% split.
const refineBudgetCutSum = (
  cuts: {
    budgetCutAccommodation?: number | null;
    budgetCutActivities?: number | null;
    budgetCutDinning?: number | null;
    budgetCutLogistic?: number | null;
  },
  ctx: z.RefinementCtx
) => {
  const cutEntries: Array<[string, number | null | undefined]> = [
    ['budgetCutAccommodation', cuts.budgetCutAccommodation],
    ['budgetCutActivities', cuts.budgetCutActivities],
    ['budgetCutDinning', cuts.budgetCutDinning],
    ['budgetCutLogistic', cuts.budgetCutLogistic],
  ];
  const presentValues = cutEntries
    .map(([, value]) => value)
    .filter((value): value is number => typeof value === 'number');
  if (presentValues.length === 0) return;
  if (presentValues.length < cutEntries.length) {
    const missing = cutEntries
      .filter(([, value]) => typeof value !== 'number')
      .map(([name]) => name);
    ctx.addIssue({
      code: 'custom',
      message: `Budget cut percentages must be all set or all omitted (missing: ${missing.join(', ')})`,
      path: ['budgetCutAccommodation'],
    });
    return;
  }
  const sum = presentValues.reduce((acc, value) => acc + value, 0);
  if (sum !== 100) {
    ctx.addIssue({
      code: 'custom',
      message: `Budget cut percentages must sum to 100, but sum to ${sum}`,
      path: ['budgetCutAccommodation'],
    });
  }
};

// XT-471 follow-up: per-organisation rates for the Surcharge / Agency
// Commission pricing blocks. Only the ALLOWED SET and the DEFAULT offered for a
// NEW line are configured here — the rate a quote was actually priced with lives
// on the line itself (`itinerary_version.pricing_adjustments`), so narrowing an
// org's list never reprices an existing itinerary.
//
// Absent ⇒ the product defaults (`PRICING_SURCHARGE_DEFAULT_PERCENT`,
// `PRICING_AGENCY_COMMISSION_PRESET_PERCENTS` in
// `@travel-planner/shared/pricing`), so every pre-existing org keeps today's
// behaviour with no backfill.
//
// Spec: `apps/itineraries/prd/pricing-surcharge-agency-commission.md`.

// A Services agency commission is a gross-up (`base / (1 - rate)`), undefined at
// 100% and refunding the agency below 0 — the same `0 <= percent < 100` window
// the engine guards with `isPricingAdjustmentPercentInRange`. `.max` cannot
// express an exclusive bound, so the range is asserted in the refine below.
const adjustmentPercentSchema = z
  .number()
  .min(0)
  .lt(100)
  .refine((value) => Number.isFinite(value), {
    message: 'Percent must be a finite number',
  });

// Ten is a UI bound, not a domain one: a dropdown of allowed rates stops being a
// dropdown past that.
const adjustmentPercentListSchema = z
  .array(adjustmentPercentSchema)
  .min(1)
  .max(10);

const adjustmentBlockSettingsSchema = z
  .object({
    allowed: adjustmentPercentListSchema,
    default: adjustmentPercentSchema,
  })
  .superRefine((block, ctx) => {
    const unique = new Set(block.allowed);
    if (unique.size !== block.allowed.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'Allowed percentages must not contain duplicates',
        path: ['allowed'],
      });
    }
    // The default seeds a new line, and that line's percent must be selectable
    // in the picker — otherwise adding a line shows one rate while the draft
    // holds another.
    if (!block.allowed.includes(block.default)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Default percentage must be one of the allowed percentages',
        path: ['default'],
      });
    }
  });

export const pricingAdjustmentSettingsSchema = z.object({
  surcharge: adjustmentBlockSettingsSchema.optional(),
  agencyCommission: adjustmentBlockSettingsSchema.optional(),
  // XT-964 — the agency's commission per category; a category without a rate
  // pays `agencyCommission.default`.
  agencyCommissionByCategory: commissionDealsSchema.optional(),
});

export type PricingAdjustmentSettings = z.infer<
  typeof pricingAdjustmentSettingsSchema
>;

// XT-782 — the Traveller-opened-the-preview follow-up email to the travel
// agent. Every knob an org admin can turn lives here; the env only supplies the
// fallback delay, so an org that has never opened this form still behaves.
//
// `viewerOrdinal` is the product heuristic, kept CONFIGURABLE rather than
// hardcoded to 3: the assumption is that the 1st distinct viewer of a preview is
// the consultant and the 2nd is the customer, so the 3rd is the traveller. That
// holds for the common sales flow and not for every org, so an org whose
// consultants never open the traveller link sets 2, and an org that wants the
// very first open sets 1. It counts DISTINCT masked IPs on the active version,
// never raw view rows, so a refresh or a multi-asset page load cannot advance it.
export const previewFollowUpSettingsSchema = z.object({
  // Absent or false = the whole feature is off for this org. Opt-in, so no
  // organization starts emailing its agents because of a deploy.
  enabled: z.boolean().optional(),
  // Which distinct viewer of the active version is assumed to be the traveller.
  // 1-10: below 1 is meaningless and above 10 would never fire in practice.
  viewerOrdinal: z.number().int().min(1).max(10).optional(),
  // Hours between that view and the email. 0 is legal and means "send on the
  // next sweep" — the immediate-send button is a separate, explicit action.
  delayHours: z.number().min(0).max(720).optional(),
  // The editable template. `null`/absent falls back to
  // DEFAULT_PREVIEW_FOLLOW_UP_TEMPLATE so an org that never edits it still
  // sends sensible copy.
  subject: z.string().max(300).nullable().optional(),
  body: z.string().max(10_000).nullable().optional(),
});

export type PreviewFollowUpSettings = z.infer<
  typeof previewFollowUpSettingsSchema
>;

// The placeholders a master admin may use in the subject/body above. Rendering
// is a literal find-and-replace on these exact tokens — deliberately NOT a
// template engine, because the copy is authored by non-developers in a plain
// textarea and an engine would turn a typo into an error or an injection.
// An unknown token is left verbatim rather than blanked, so a typo is VISIBLE
// in the test send instead of silently producing a hole in the sentence.
export const PREVIEW_FOLLOW_UP_PLACEHOLDERS = [
  'CUSTOMER_First_name',
  'TRAVELLER_First_Name',
  'ITINERARY_Title',
  'OWNER_First_name',
] as const;

export type PreviewFollowUpPlaceholder =
  (typeof PREVIEW_FOLLOW_UP_PLACEHOLDERS)[number];

/** Verbatim from the XT-782 thread — the copy the stakeholders signed off. */
export const DEFAULT_PREVIEW_FOLLOW_UP_SUBJECT =
  '(TRAVELLER_First_Name) has opened the preview for (ITINERARY_Title)';

export const DEFAULT_PREVIEW_FOLLOW_UP_BODY = `Hi (CUSTOMER_First_name),

We just noticed that (TRAVELLER_First_Name) has seen the Preview for (ITINERARY_Title)! I would love to hear if they have shared any thoughts or feedback so far.

Thank you!
(OWNER_First_name)`;

export const subitemDefaultVatSchema = z.object({
  Transport: z.uuid().nullable().optional(),
  Guide: z.uuid().nullable().optional(),
  Ticket: z.uuid().nullable().optional(),
  Other: z.uuid().nullable().optional(),
});

export type SubitemDefaultVat = z.infer<typeof subitemDefaultVatSchema>;

export const organizationSettingsSchema = z
  .object({
    supportPhoneNumber: z.string().nullable().optional(),
    logoKey: z.string().nullable().optional(),
    currency: z.string().length(3).optional(),
    subdomain: z
      .string()
      .regex(SUBDOMAIN_REGEX, 'Invalid subdomain format')
      .optional(),
    budgetCutAccommodation: z.number().min(0).max(100).nullable().optional(),
    budgetCutActivities: z.number().min(0).max(100).nullable().optional(),
    budgetCutDinning: z.number().min(0).max(100).nullable().optional(),
    budgetCutLogistic: z.number().min(0).max(100).nullable().optional(),
    itinerary_value_categories: z
      .array(itineraryValueCategorySchema)
      .optional(),
    color_palette: colorPaletteSettingsSchema.optional(),
    publishTimeSpan: publishTimeSpanSchema.nullable().optional(),
    cmsEmailAddress: z.email().nullable().optional(),
    heroMedia: z
      .record(z.string(), z.record(z.string(), heroMediaItemSchema))
      .optional(),
    companyInfo: z.array(companyInfoLineSchema).optional(),
    language: organizationLanguageSchema.optional(),
    budgetRequired: z.boolean().optional(),
    budgetValueThresholds: budgetValueThresholdsSchema.optional(),
    contactFieldVisibility: z.record(z.string(), z.boolean()).optional(),
    // INBOUND: whether THIS org uses shared resources (accounts/companies,
    // contacts, layout templates) belonging to OTHER organizations. False means
    // "show me only my own records" — it is a consumer-side view filter and
    // never controls what other organizations can see of this org's records.
    //
    // There is deliberately NO outbound equivalent: the only org that withholds
    // its records from everyone is the demo org, gated by DEMO_ORGANIZATION_ID
    // (env, not settings) so its isolation cannot be switched off from the UI.
    //
    // Consuming shared resources is the historical default (TCC01-003), so an
    // org that has never set the key must keep seeing them: every read site
    // tests `settings?.shareResources !== false`, NOT `=== true`. A Zod
    // `.default` would not help — existing rows have no key at all, and
    // `.optional()` is what lets a partial `settings` patch omit it. Suppliers
    // are never shared regardless (TCC01-006).
    shareResources: z.boolean().optional(),
    // XT-380 /operations metric-card defaults — languages treated as "default"
    // on the Guides Languages card. When absent the FE falls back to EN/ES/PT.
    //
    // `priorityVenues` used to sit here as well. XT-538 replaced it with
    // `itineraries.venue.is_priority`, which has an admin UI; the key is
    // stripped from stored settings by that ticket's migration.
    opsDashboard: z
      .object({
        guideLanguageExclude: z.array(z.string()).optional(),
      })
      .optional(),
    // XT-600 — per-country rollout of the hotel supplier-reservation email.
    // Holds the countries an org has switched the new hotel template on for;
    // an Accommodation item in any other country keeps the generic email.
    // Absent or empty = off everywhere, so an org opts in deliberately.
    // Values are country NAMES as stored on `itinerary_item.country`
    // ("Morocco"), matched case-insensitively against the org's CMS landing
    // pages (`landing/<country>`), which is where the desk list comes from.
    hotelReservationEmailCountries: z.array(z.string()).optional(),
    // Optigest external-API per-org key — the SHA-256 hash of the key.
    // SERVER-MANAGED: written only by the generateOptigestKey/revokeOptigestKey
    // mutations, never accepted from the generic organizations.update input, and
    // stripped from every client-facing projection.
    optigestToken: z.string().nullable().optional(),
    // Product API (`/api/v2/products*`, `/api/v2/catalogs`) per-org key — the
    // SHA-256 hash of the key. A SEPARATE credential from `optigestToken`:
    // the product surface serves external consumers (e.g. TempoVIP finance
    // agents) that have nothing to do with the Optigest accounting
    // integration, so one leaking or being revoked must not affect the other.
    // SERVER-MANAGED: written only by the generateProductApiKey /
    // revokeProductApiKey mutations, never accepted from the generic
    // organizations.update input, and stripped from every client projection.
    productApiToken: z.string().nullable().optional(),
    // Product API IP allow-list — the IPv4/IPv6 addresses and CIDR ranges
    // permitted to call the product endpoints with this org's Product API
    // key. Unlike the token this is NOT a secret (the admin must read it back
    // to edit it), so it stays on the client projection. SERVER-MANAGED all
    // the same: written only by setProductApiAllowedIps.
    // EMPTY OR ABSENT = NO RESTRICTION — an org opts in deliberately.
    productApiAllowedIps: z.array(z.string()).nullable().optional(),
    // MCP planner surface (`xt-planner`, apps/mcp) per-org key — the SHA-256
    // hash of the key. A THIRD credential, separate from `optigestToken` and
    // `productApiToken`, and the only one of the three that opens a surface
    // which WRITES: it lets a consultant's own Claude client build and commit
    // itineraries against this organisation over HTTP.
    // ONE KEY PER ORGANISATION, SHARED BY ITS CONSULTANTS. That is a deliberate
    // pilot-stage trade and it has a cost worth stating where the field is
    // declared: the ownership register can name the organisation that wrote an
    // itinerary and never the person, and revoking rotates for everyone at once.
    // SERVER-MANAGED: written only by the generateMcpPlannerKey /
    // revokeMcpPlannerKey mutations, never accepted from the generic
    // organizations.update input, and stripped from every client projection.
    mcpPlannerToken: z.string().nullable().optional(),
    // Travefy itinerary-export credentials (XT-163). One pair per org —
    // Travefy bills/keys per customer account. SERVER-MANAGED: written only by
    // setTravefyCredentials/clearTravefyCredentials, never accepted from the
    // generic organizations.update input, and stripped from every client-
    // facing projection (raw values are needed for outbound API calls).
    travefyApiPublicKey: z.string().nullable().optional(),
    travefyUserToken: z.string().nullable().optional(),
    // Axus itinerary-export credentials — `{ clientId, username, password }`
    // as ONE AES-256-GCM ciphertext (`integration-secret-crypto`, key
    // `INTEGRATION_SECRETS_ENC_KEY`, bound to this org's id). SERVER-MANAGED:
    // written only by setAxusCredentials/clearAxusCredentials, never accepted
    // from the generic organizations.update input, stripped from every client
    // projection and from audit rows. Absent = the org has no credentials of
    // its own (only TempoVIP then falls back to the platform AXUS_* env).
    axusCredentials: z.string().nullable().optional(),
    // Org-configurable rates for the itinerary pricing Surcharge / Agency
    // Commission blocks. See `pricingAdjustmentSettingsSchema` above.
    pricingAdjustments: pricingAdjustmentSettingsSchema.optional(),
    // XT-782 — the "your traveller opened the preview" follow-up email sent to
    // the CUSTOMER (the travel agent). See `previewFollowUpSettingsSchema`.
    previewFollowUp: previewFollowUpSettingsSchema.optional(),
    // XT-837 — default VAT (a `vat_tax_setting` id, used for both cost and sell
    // VAT) per subitem type. Pre-fills new subitems and product subitem
    // templates; absent / null = no default.
    subitemDefaultVat: subitemDefaultVatSchema.optional(),
  })
  .superRefine(refineBudgetCutSum);

export type OrganizationSettings = z.infer<typeof organizationSettingsSchema>;

export const axusCredentialSourceSchema = z.enum([
  'organization',
  'platform',
  'none',
]);
export type AxusCredentialSource = z.infer<typeof axusCredentialSourceSchema>;

export const organizationSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  active: z.boolean(),
  aliases: z.array(z.string()).nullable(),
  isTesting: z.boolean(),
  onboardingDocumentation: z.unknown().nullable(),
  embedLinkIds: z.unknown().nullable(),
  supportPhoneNumber: z.string().nullable(),
  logoKey: z.string().nullable(),
  settings: organizationSettingsSchema.nullable(),
  // Whether an Optigest per-org API key is currently set. Derived server-side
  // from settings.optigestToken (which is never sent to clients).
  hasOptigestApiKey: z.boolean(),
  // Whether a Product API per-org key is currently set. Derived server-side
  // from settings.productApiToken (which is never sent to clients).
  hasProductApiKey: z.boolean(),
  // Whether an MCP planner per-org key is currently set. Derived server-side
  // from settings.mcpPlannerToken (which is never sent to clients).
  hasMcpPlannerKey: z.boolean(),
  // Whether this org has both Travefy credentials set. Derived server-side
  // from settings.travefyApiPublicKey + travefyUserToken (never sent to clients).
  // The itinerary header uses this to disable "Export to Travefy" until keys
  // are assigned (XT-163).
  hasTravefyConfigured: z.boolean(),
  // Which Axus account "Export to Axus" pushes to — derived server-side, the
  // credentials themselves never reach clients. `organization` = the org's own
  // credentials; `platform` = no own credentials, but this is TempoVIP, the one
  // org allowed to fall back to the platform AXUS_* env; `none` = export is
  // disabled until an admin assigns credentials in Company Settings.
  axusCredentialSource: axusCredentialSourceSchema,
});

export type Organization = z.infer<typeof organizationSchema>;

export const organizationBrandingSchema = z.object({
  name: z.string().nullable(),
  logo: z.string().nullable(),
  currency: z.string(),
  phone: z.string().nullable(),
  subdomain: z.string().nullable(),
});

export type OrganizationBranding = z.infer<typeof organizationBrandingSchema>;

export const embedLinkIdsResponseSchema = z.object({
  embedLinksIds: z.array(z.uuid()),
  organizationName: z.string(),
});

export type EmbedLinkIdsResponse = z.infer<typeof embedLinkIdsResponseSchema>;

export const updateOrganizationInput = z.object({
  id: z.uuid(),
  settings: organizationSettingsSchema.optional(),
});

export type UpdateOrganizationInput = z.infer<typeof updateOrganizationInput>;

// Optigest per-org API-key mutations. Input carries only the target org id —
// the key is generated server-side and returned once; there is no editable
// key field (add / replace / remove only).
export const optigestKeyInput = z.object({ id: z.uuid() });
export type OptigestKeyInput = z.infer<typeof optigestKeyInput>;

// Product API per-org key mutations. Same add / replace / remove shape as the
// Optigest key, but a separate credential for a separate surface.
export const productApiKeyInput = z.object({ id: z.uuid() });
export type ProductApiKeyInput = z.infer<typeof productApiKeyInput>;

export const generateProductApiKeyOutput = z.object({ rawKey: z.string() });
export type GenerateProductApiKeyOutput = z.infer<
  typeof generateProductApiKeyOutput
>;

// MCP planner per-org key mutations. Same add / replace / remove shape as the
// two above, and a third separate credential — this one opens the write
// surface `xt-planner` serves to a consultant's own Claude client.
export const mcpPlannerKeyInput = z.object({ id: z.uuid() });
export type McpPlannerKeyInput = z.infer<typeof mcpPlannerKeyInput>;

export const generateMcpPlannerKeyOutput = z.object({ rawKey: z.string() });
export type GenerateMcpPlannerKeyOutput = z.infer<
  typeof generateMcpPlannerKeyOutput
>;

// Product API per-org IP allow-list. Entries are single IPv4/IPv6 addresses or
// CIDR ranges, validated with the SAME parser the API matches against
// (`isValidAllowlistEntry`) so the UI can never store an entry that could
// not match. An empty array clears the restriction (allow all).
export const PRODUCT_API_MAX_ALLOWED_IPS = 50;

export const setProductApiAllowedIpsInput = z.object({
  id: z.uuid(),
  ips: z
    .array(
      z
        .string()
        .trim()
        .refine(isValidAllowlistEntry, 'Not a valid IP address or CIDR range')
    )
    .max(PRODUCT_API_MAX_ALLOWED_IPS),
});
export type SetProductApiAllowedIpsInput = z.infer<
  typeof setProductApiAllowedIpsInput
>;

// Travefy per-org credentials (XT-163). Keys come from Travefy
// (apissales@travefy.com) — operators paste them; we store the values so the
// export can call Travefy on behalf of this org. Both fields required to set;
// clear removes the pair.
export const setTravefyCredentialsInput = z.object({
  id: z.uuid(),
  apiPublicKey: z.string().trim().min(1),
  userToken: z.string().trim().min(1),
});
export type SetTravefyCredentialsInput = z.infer<
  typeof setTravefyCredentialsInput
>;

export const clearTravefyCredentialsInput = z.object({ id: z.uuid() });
export type ClearTravefyCredentialsInput = z.infer<
  typeof clearTravefyCredentialsInput
>;

// Axus per-org credentials. All three come from the org's Axus account; the
// set mutation encrypts them together, clear removes them.
export const setAxusCredentialsInput = z.object({
  id: z.uuid(),
  clientId: z.string().trim().min(1),
  username: z.string().trim().min(1),
  // Not trimmed — a password may legitimately start or end with a space.
  password: z.string().min(1),
});
export type SetAxusCredentialsInput = z.infer<typeof setAxusCredentialsInput>;

export const clearAxusCredentialsInput = z.object({ id: z.uuid() });
export type ClearAxusCredentialsInput = z.infer<
  typeof clearAxusCredentialsInput
>;

// Copy the platform (TempoVIP) Axus account into the org's own credentials —
// System users only; the values are copied server-side, never sent here.
export const copyPlatformAxusCredentialsInput = z.object({ id: z.uuid() });
export type CopyPlatformAxusCredentialsInput = z.infer<
  typeof copyPlatformAxusCredentialsInput
>;

export const generateOptigestKeyOutput = z.object({ rawKey: z.string() });
export type GenerateOptigestKeyOutput = z.infer<
  typeof generateOptigestKeyOutput
>;

export const updateBudgetSettingsInput = z
  .object({
    id: z.uuid(),
    budgetRequired: z.boolean().optional(),
    budgetCutAccommodation: z.number().min(0).max(100).optional(),
    budgetCutActivities: z.number().min(0).max(100).optional(),
    budgetCutDinning: z.number().min(0).max(100).optional(),
    budgetCutLogistic: z.number().min(0).max(100).optional(),
    itineraryValueCategories: z.array(itineraryValueCategorySchema).optional(),
    budgetValueThresholds: budgetValueThresholdsSchema.optional(),
  })
  .superRefine(refineBudgetCutSum);

export type UpdateBudgetSettingsInput = z.infer<
  typeof updateBudgetSettingsInput
>;

// Deliberately NOT an extension of `updateBudgetSettingsInput`: that schema
// carries `refineBudgetCutSum`, which requires the four budget cuts to sum to
// exactly 100. Reusing it would make an unrelated rate change fail unless the
// caller also round-tripped a valid set of budget cuts.
//
// Each block is optional so a save can touch one without clearing the other; the
// service merges per block.
export const updatePricingAdjustmentSettingsInput = z.object({
  id: z.uuid(),
  surcharge: adjustmentBlockSettingsSchema.optional(),
  agencyCommission: adjustmentBlockSettingsSchema.optional(),
  agencyCommissionByCategory: commissionDealsSchema.optional(),
});

export type UpdatePricingAdjustmentSettingsInput = z.infer<
  typeof updatePricingAdjustmentSettingsInput
>;

// XT-837 — replaces the whole per-type default VAT map.
export const updateSubitemDefaultVatInput = z.object({
  id: z.uuid(),
  subitemDefaultVat: subitemDefaultVatSchema,
});

export type UpdateSubitemDefaultVatInput = z.infer<
  typeof updateSubitemDefaultVatInput
>;

export const updateEmbedLinkIdsInput = z.object({
  embedLinkIds: z.array(z.uuid()),
});

export type UpdateEmbedLinkIdsInput = z.infer<typeof updateEmbedLinkIdsInput>;

export const updateSupportPhoneNumberInput = z.object({
  supportPhoneNumber: z.string(),
});

export type UpdateSupportPhoneNumberInput = z.infer<
  typeof updateSupportPhoneNumberInput
>;

export const brandingInput = z.object({
  subdomain: z.string(),
});

export type BrandingInput = z.infer<typeof brandingInput>;

export const findOneOrganizationInput = z.object({
  id: z.uuid(),
});

export type FindOneOrganizationInput = z.infer<typeof findOneOrganizationInput>;

export const getEmbedLinkIdsInput = z.object({
  organizationId: z.uuid(),
});

export type GetEmbedLinkIdsInput = z.infer<typeof getEmbedLinkIdsInput>;

export const updateOrganizationResultSchema = z.object({
  success: z.boolean(),
});

export type UpdateOrganizationResult = z.infer<
  typeof updateOrganizationResultSchema
>;

// Organization initialization (TP-2909) — ported 1:1 from legacy
// `OrganizationInitializationStatusDto` / `…ResultDto`. A new org with no
// itinerary gets an "Initialize" action that clones a complete baseline from
// the TempoVIP template org. `isInitialized` mirrors legacy: all four core
// entities present (layout, customer, product, itinerary).
export const organizationInitStatusSchema = z.object({
  hasLayoutTemplate: z.boolean(),
  hasCustomer: z.boolean(),
  hasProduct: z.boolean(),
  hasItinerary: z.boolean(),
  isInitialized: z.boolean(),
});

export type OrganizationInitStatus = z.infer<
  typeof organizationInitStatusSchema
>;

const initializedItemSchema = z.object({ id: z.uuid() });

// `created` / `alreadyExisted` carry the camelCase entity keys
// (`layoutTemplate`, `customer`, `product`, `itinerary`) the result modal
// prettifies. Matches legacy `OrganizationInitializationResultDto`.
// `settingsSeeded` extends legacy: human-readable UI group labels for the
// org `settings` seeded from TempoVIP (Category Colors, Budget Settings,
// Product Value Category, Itinerary Value Categories) so the modal can report
// the configuration that was applied. Empty when nothing was seeded.
export const organizationInitResultSchema = z.object({
  success: z.boolean(),
  layoutTemplate: initializedItemSchema.optional(),
  customer: initializedItemSchema.optional(),
  product: initializedItemSchema.optional(),
  itinerary: initializedItemSchema.optional(),
  regionalVideo: initializedItemSchema.optional(),
  alreadyExisted: z.array(z.string()),
  created: z.array(z.string()),
  settingsSeeded: z.array(z.string()).default([]),
});

export type OrganizationInitResult = z.infer<
  typeof organizationInitResultSchema
>;
